import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import SettingsIcon from '@mui/icons-material/Settings';
import Menu from '@mui/material/Menu';
import Toast from '../../common/Toast';
import { useToast } from '../../hooks/useToast';
import { harmonateTokens as ht } from '../../../theme/harmonateTokens';
import {
  harmonatePageSx,
  harmonatePageTitleSx,
  harmonateCardSx,
  harmonateProceedButtonSx,
  harmonateCancelButtonSx,
  harmonateToolbarBuFieldSx,
  harmonatePlainIconButtonSx,
  harmonateSwitchSx,
  harmonateDialogPaperProps,
  harmonateDialogTitleSx,
  harmonateDialogFormContentSx,
  harmonateFormFieldsSx,
  harmonateTextFieldSx,
  harmonateInputLabelProps,
  harmonateSelectMenuProps,
} from '../../../theme/harmonateStyles';
import { useFunctionPermissions } from '../../../hooks/useFunctionPermissions';
import { FC } from '../../../constants/functionCodes';
import { isHarmonateUser as isServerHarmonateUser } from '../../../auth/serverIdentity';

const STATUS_META = {
  NOT_STARTED: { label: 'Not Started', color: ht.fadedText, bg: ht.listBg },
  BLOCKED: { label: 'Blocked', color: '#B45309', bg: 'rgba(245, 158, 11, 0.15)' },
  IN_PROGRESS: { label: 'In Progress', color: ht.blue, bg: 'rgba(46, 166, 160, 0.15)' },
  COMPLETED: { label: 'Completed', color: ht.green, bg: 'rgba(2, 107, 29, 0.12)' },
  // High-contrast on dark page legend AND readable on white grid cells
  HISTORICAL: { label: 'Historical', color: '#334155', bg: '#E2E8F0' },
};

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`,
  'Content-Type': 'application/json',
});

const CashReconStatusDashboard = () => {
  const navigate = useNavigate();
  const { canEdit } = useFunctionPermissions(FC.CASH_RECONCILIATION);
  const { toastState, showSuccess, showError, hideToast } = useToast();
  const isHarmonateUser = isServerHarmonateUser();

  const [loading, setLoading] = useState(true);
  const [cells, setCells] = useState([]);
  const [properties, setProperties] = useState([]);
  const [propertyFilter, setPropertyFilter] = useState(null);
  const [glFilter, setGlFilter] = useState('');
  const [showHistory, setShowHistory] = useState(false);
  const [error, setError] = useState(null);

  const [menuAnchor, setMenuAnchor] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settingsProperty, setSettingsProperty] = useState(null);
  const [goLiveMonth, setGoLiveMonth] = useState('');
  const [lastNMonths, setLastNMonths] = useState(3);
  const [settingsSaving, setSettingsSaving] = useState(false);

  const [overrideDialog, setOverrideDialog] = useState(null);
  const [overrideSaving, setOverrideSaving] = useState(false);

  const tenantId = localStorage.getItem('tenant_id');

  const formatPropertyLabel = useCallback((p) => {
    if (!p) return '';
    const code = String(p.propertyCode || p.PROPERTY_CODE || '').trim();
    let name = String(p.propertyName || p.PROPERTY_NAME || '').trim();
    // Avoid "CODE - CODE - Name" if name already includes the code
    if (code && name) {
      const prefix = `${code} - `;
      if (name.toUpperCase().startsWith(prefix.toUpperCase())) {
        name = name.slice(prefix.length).trim();
      }
      return `${code} - ${name}`;
    }
    return name || code || '';
  }, []);

  const propertyLabelById = useMemo(() => {
    const map = {};
    properties.forEach((p) => {
      const id = String(p.propertyId || p.PROPERTY_ID || '');
      if (!id) return;
      map[id] = formatPropertyLabel(p);
    });
    return map;
  }, [properties, formatPropertyLabel]);

  const fetchProperties = useCallback(async () => {
    if (!tenantId) return;
    try {
      // Prefer full tenant property master so header propertyIds always resolve to names
      const resp = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}conductor/cash-recon/properties/by-tenant?tenant_id=${tenantId}`,
        { headers: authHeaders() }
      );
      if (resp.ok) {
        const data = await resp.json();
        setProperties(Array.isArray(data) ? data : []);
        return;
      }
      const userId = localStorage.getItem('user_id');
      const fallback = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}conductor/user-tenant-properties?tenant_id=${tenantId}&user_id=${userId}`,
        { headers: authHeaders() }
      );
      if (!fallback.ok) throw new Error('Failed to load properties');
      const data = await fallback.json();
      setProperties(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      setProperties([]);
    }
  }, [tenantId]);

  const fetchGrid = useCallback(async () => {
    if (!tenantId) {
      setError('No tenant selected');
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const params = new URLSearchParams({
        tenant_id: tenantId,
        include_historical: String(showHistory),
      });
      if (propertyFilter?.propertyId || propertyFilter?.PROPERTY_ID) {
        params.set('property_id', propertyFilter.propertyId || propertyFilter.PROPERTY_ID);
      }
      if (glFilter.trim()) {
        params.set('gl_account_no', glFilter.trim());
      }
      const resp = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}conductor/cash-recon/status/grid?${params}`,
        { headers: authHeaders() }
      );
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({}));
        throw new Error(body.error || `Failed to load status grid (${resp.status})`);
      }
      const data = await resp.json();
      setCells(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || 'Failed to load status grid');
      setCells([]);
    } finally {
      setLoading(false);
    }
  }, [tenantId, showHistory, propertyFilter, glFilter]);

  useEffect(() => {
    fetchProperties();
  }, [fetchProperties]);

  useEffect(() => {
    fetchGrid();
  }, [fetchGrid]);

  const months = useMemo(() => {
    const set = new Set(cells.map((c) => c.monthYear).filter(Boolean));
    return Array.from(set).sort();
  }, [cells]);

  const rows = useMemo(() => {
    const map = new Map();
    cells.forEach((cell) => {
      const pid = String(cell.propertyId || '');
      const key = `${pid}|${cell.glAccountNo || ''}|${cell.bankAccountNo || ''}`;
      if (!map.has(key)) {
        // Always "CODE - Name" when code is known (same as Account Selection)
        const propertyLabel =
          propertyLabelById[pid]
          || formatPropertyLabel({ propertyName: cell.propertyName })
          || cell.propertyName
          || pid;
        map.set(key, {
          key,
          propertyId: cell.propertyId,
          propertyLabel,
          glAccountNo: cell.glAccountNo || '—',
          glAccountName: cell.glAccountName || '',
          bankAccountNo: cell.bankAccountNo || '',
          byMonth: {},
        });
      }
      map.get(key).byMonth[cell.monthYear] = cell;
    });
    return Array.from(map.values()).sort((a, b) =>
      String(a.propertyLabel).localeCompare(String(b.propertyLabel))
    );
  }, [cells, propertyLabelById, formatPropertyLabel]);

  const statusCounts = useMemo(() => {
    const counts = {
      NOT_STARTED: 0,
      BLOCKED: 0,
      IN_PROGRESS: 0,
      COMPLETED: 0,
      HISTORICAL: 0,
    };
    cells.forEach((c) => {
      if (counts[c.status] != null) counts[c.status] += 1;
    });
    return counts;
  }, [cells]);

  const openMapping = (cell) => {
    if (!cell) return;
    if (cell.status === 'BLOCKED' && !cell.adminOverride) {
      showError(cell.blockedMessage || 'This month is blocked until the prior month is Completed.');
      return;
    }
    if (cell.status === 'HISTORICAL' && !cell.adminOverride) {
      showError('Historical months are read-only. Use Admin override to change.');
      return;
    }
    if (cell.status === 'COMPLETED' && !isHarmonateUser) {
      // Allow open for view; Mapping still cannot reopen without override
    }
    const prop = properties.find(
      (p) => String(p.propertyId || p.PROPERTY_ID) === String(cell.propertyId)
    );
    // Pass plain name + id only — Mapping hotel dropdown matches PROPERTY_NAME, not "CODE - Name"
    const propertyName = prop?.propertyName || prop?.PROPERTY_NAME || '';
    navigate('/cash-recon/mapping', {
      state: {
        propertyId: cell.propertyId,
        propertyName,
        propertyCode: prop?.propertyCode || prop?.PROPERTY_CODE || '',
        monthYear: cell.monthYear,
        glAccountNo: cell.glAccountNo,
        bankAccountNo: cell.bankAccountNo,
        reconStatus: cell.status,
        headerId: cell.headerId,
        blockedMessage: cell.blockedMessage,
      },
    });
  };

  const openSettings = async () => {
    setMenuAnchor(null);
    if (!isHarmonateUser) {
      showError('Only Harmonate users can set property go-live month.');
      return;
    }
    setSettingsProperty(propertyFilter || properties[0] || null);
    setGoLiveMonth('');
    setLastNMonths(3);
    setSettingsOpen(true);
    const pid = (propertyFilter || properties[0])?.propertyId
      || (propertyFilter || properties[0])?.PROPERTY_ID;
    if (!pid || !tenantId) return;
    try {
      const resp = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}conductor/cash-recon/status/settings/by-property?tenant_id=${tenantId}&property_id=${pid}`,
        { headers: authHeaders() }
      );
      if (resp.ok) {
        const data = await resp.json();
        setGoLiveMonth(data.goLiveMonth || '');
        setLastNMonths(data.lastNMonths || 3);
      }
    } catch {
      /* empty */
    }
  };

  const loadSettingsForProperty = async (prop) => {
    setSettingsProperty(prop);
    const pid = prop?.propertyId || prop?.PROPERTY_ID;
    if (!pid || !tenantId) return;
    try {
      const resp = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}conductor/cash-recon/status/settings/by-property?tenant_id=${tenantId}&property_id=${pid}`,
        { headers: authHeaders() }
      );
      if (resp.ok) {
        const data = await resp.json();
        setGoLiveMonth(data.goLiveMonth || '');
        setLastNMonths(data.lastNMonths || 3);
      } else {
        setGoLiveMonth('');
        setLastNMonths(3);
      }
    } catch {
      setGoLiveMonth('');
      setLastNMonths(3);
    }
  };

  const saveSettings = async () => {
    const pid = settingsProperty?.propertyId || settingsProperty?.PROPERTY_ID;
    if (!pid || !goLiveMonth) {
      showError('Select a property and go-live month (YYYY-MM).');
      return;
    }
    try {
      setSettingsSaving(true);
      const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}conductor/cash-recon/status/settings`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({
          tenantId,
          propertyId: pid,
          goLiveMonth,
          lastNMonths: Number(lastNMonths) || 3,
          updatedBy: localStorage.getItem('user_id'),
        }),
      });
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({}));
        throw new Error(body.error || 'Failed to save settings');
      }
      showSuccess('Go-live settings saved');
      setSettingsOpen(false);
      fetchGrid();
    } catch (err) {
      showError(err.message || 'Failed to save settings');
    } finally {
      setSettingsSaving(false);
    }
  };

  const applyAdminOverride = async () => {
    if (!overrideDialog?.headerId) {
      showError('Create / open the mapping first, then apply Admin override on the header.');
      return;
    }
    try {
      setOverrideSaving(true);
      const resp = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}conductor/cash-recon/status/headers/${overrideDialog.headerId}/admin-override`,
        {
          method: 'POST',
          headers: authHeaders(),
          body: JSON.stringify({
            adminOverride: true,
            updatedBy: localStorage.getItem('user_id'),
          }),
        }
      );
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({}));
        throw new Error(body.error || 'Failed to set admin override');
      }
      showSuccess('Admin override applied');
      setOverrideDialog(null);
      fetchGrid();
    } catch (err) {
      showError(err.message || 'Failed to set admin override');
    } finally {
      setOverrideSaving(false);
    }
  };

  const renderStatusChip = (cell) => {
    if (!cell) {
      return null;
    }
    const meta = STATUS_META[cell.status] || STATUS_META.NOT_STARTED;
    return (
      <Tooltip
        title={
          cell.blockedMessage
            || `${meta.label}${cell.glAccountNo ? ` · GL ${cell.glAccountNo}` : ''}`
        }
      >
        <Chip
          size="small"
          label={meta.label}
          onClick={() => openMapping(cell)}
          onContextMenu={(e) => {
            if (!isHarmonateUser) return;
            e.preventDefault();
            if (cell.status === 'BLOCKED' || cell.status === 'HISTORICAL' || cell.status === 'COMPLETED') {
              setOverrideDialog(cell);
            }
          }}
          sx={{
            bgcolor: meta.bg,
            color: meta.color,
            fontWeight: 600,
            fontSize: '0.7rem',
            height: 24,
            cursor: 'pointer',
            maxWidth: '100%',
            '& .MuiChip-label': { px: 1 },
          }}
        />
      </Tooltip>
    );
  };

  return (
    <Box sx={{ ...harmonatePageSx, height: '100%', minHeight: 0 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexShrink: 0, gap: 2, flexWrap: 'wrap' }}>
        <Box>
          <Typography variant="h5" sx={harmonatePageTitleSx}>
            Cash Reconciliation
          </Typography>
          <Typography variant="body2" sx={{ color: ht.white, mt: 0.5, opacity: 0.9 }}>
            Status by property / GL / month — click a cell to open Mapping
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
          <FormControlLabel
            control={
              <Switch
                checked={showHistory}
                onChange={(e) => setShowHistory(e.target.checked)}
                sx={harmonateSwitchSx}
              />
            }
            label="Show history"
            sx={{ '& .MuiFormControlLabel-label': { color: ht.white, fontSize: '0.875rem', whiteSpace: 'nowrap' } }}
          />
          <Autocomplete
            size="small"
            options={properties}
            value={propertyFilter}
            onChange={(_, v) => setPropertyFilter(v)}
            getOptionLabel={(p) => formatPropertyLabel(p)}
            isOptionEqualToValue={(a, b) =>
              String(a?.propertyId || a?.PROPERTY_ID) === String(b?.propertyId || b?.PROPERTY_ID)
            }
            renderInput={(params) => (
              <TextField {...params} label="Property" placeholder="All properties" sx={{ minWidth: 240 }} />
            )}
            sx={{
              ...harmonateToolbarBuFieldSx,
              minWidth: 240,
              '& .MuiOutlinedInput-root': {
                bgcolor: 'transparent',
                color: ht.white,
                height: 40,
                '& fieldset': { borderColor: 'rgba(255,255,255,0.55)' },
              },
              '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.85)' },
              '& .MuiOutlinedInput-input': { color: ht.white },
            }}
          />
          <TextField
            size="small"
            label="GL Account"
            value={glFilter}
            onChange={(e) => setGlFilter(e.target.value)}
            placeholder="Filter GL"
            sx={{
              ...harmonateToolbarBuFieldSx,
              width: 160,
              '& .MuiOutlinedInput-root': {
                bgcolor: 'transparent',
                color: ht.white,
                height: 40,
                '& fieldset': { borderColor: 'rgba(255,255,255,0.55)' },
              },
              '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.85)' },
              '& .MuiOutlinedInput-input': { color: ht.white },
            }}
          />
          <Button
            variant="contained"
            onClick={() => navigate('/cash-recon/mapping')}
            sx={harmonateProceedButtonSx}
          >
            Open Mapping
          </Button>
          <IconButton onClick={(e) => setMenuAnchor(e.currentTarget)} sx={harmonatePlainIconButtonSx}>
            <MoreVertIcon />
          </IconButton>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        {Object.entries(STATUS_META).map(([key, meta]) => (
          <Chip
            key={key}
            size="small"
            label={`${meta.label}: ${statusCounts[key] || 0}`}
            sx={{ bgcolor: meta.bg, color: meta.color, fontWeight: 600 }}
          />
        ))}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper elevation={0} sx={{ ...harmonateCardSx, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', p: 0 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1, minHeight: 240 }}>
            <CircularProgress sx={{ color: ht.blue }} />
          </Box>
        ) : (
          <TableContainer sx={{ flex: 1, minHeight: 0 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, minWidth: 200, bgcolor: ht.white }}>Property</TableCell>
                  <TableCell sx={{ fontWeight: 700, minWidth: 120, bgcolor: ht.white }}>GL</TableCell>
                  <TableCell sx={{ fontWeight: 700, minWidth: 120, bgcolor: ht.white }}>Bank Acct</TableCell>
                  {months.map((m) => (
                    <TableCell key={m} align="center" sx={{ fontWeight: 700, minWidth: 110, bgcolor: ht.white }}>
                      {m}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3 + months.length} align="center" sx={{ py: 6, color: ht.fadedText }}>
                      No reconciliation rows yet. Create Account Selection mappings, or set a property go-live month
                      {isHarmonateUser ? ' (menu → Go-live settings).' : '.'}
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((row) => (
                    <TableRow key={row.key} hover>
                      <TableCell>{row.propertyLabel}</TableCell>
                      <TableCell>
                        <Typography variant="body2">{row.glAccountNo}</Typography>
                        {row.glAccountName ? (
                          <Typography variant="caption" color="text.secondary">
                            {row.glAccountName}
                          </Typography>
                        ) : null}
                      </TableCell>
                      <TableCell>{row.bankAccountNo || '—'}</TableCell>
                      {months.map((m) => (
                        <TableCell key={m} align="center">
                          {renderStatusChip(row.byMonth[m])}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem
          onClick={() => {
            setMenuAnchor(null);
            navigate('/cash-recon/account-mapping');
          }}
        >
          Account Selection
        </MenuItem>
        <MenuItem
          onClick={() => {
            setMenuAnchor(null);
            navigate('/cash-recon/compare');
          }}
        >
          Compare
        </MenuItem>
        <MenuItem
          onClick={() => {
            setMenuAnchor(null);
            navigate('/cash-recon/output');
          }}
        >
          Output
        </MenuItem>
        {isHarmonateUser ? (
          <MenuItem onClick={openSettings}>
            <SettingsIcon fontSize="small" sx={{ mr: 1 }} />
            Go-live settings
          </MenuItem>
        ) : null}
      </Menu>

      <Dialog
        open={settingsOpen}
        onClose={() => !settingsSaving && setSettingsOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={harmonateDialogPaperProps}
      >
        <DialogTitle sx={harmonateDialogTitleSx}>Cash Recon Go-live Settings</DialogTitle>
        <DialogContent sx={{ ...harmonateDialogFormContentSx, ...harmonateFormFieldsSx }}>
          <Autocomplete
            size="small"
            options={properties}
            value={settingsProperty}
            onChange={(_, v) => loadSettingsForProperty(v)}
            getOptionLabel={(p) => formatPropertyLabel(p)}
            renderInput={(params) => (
              <TextField {...params} label="Property" required InputLabelProps={harmonateInputLabelProps} />
            )}
          />
          <TextField
            size="small"
            label="Go-live month"
            type="month"
            value={goLiveMonth}
            onChange={(e) => setGoLiveMonth(e.target.value)}
            InputLabelProps={{ ...harmonateInputLabelProps, shrink: true }}
            helperText="Months before this are Historical (read-only) and do not block go-live or later months"
            sx={harmonateTextFieldSx}
            fullWidth
          />
          <TextField
            size="small"
            select
            label="Working window (last N months)"
            value={lastNMonths}
            onChange={(e) => setLastNMonths(Number(e.target.value))}
            SelectProps={{ MenuProps: harmonateSelectMenuProps }}
            helperText="Default view: current + unfinished months in this window. Show history caps at last 24 months."
            sx={harmonateTextFieldSx}
            fullWidth
          >
            {[1, 2, 3, 6, 12, 18, 24].map((n) => (
              <MenuItem key={n} value={n}>
                Last {n} month{n === 1 ? '' : 's'}
              </MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setSettingsOpen(false)} disabled={settingsSaving} sx={harmonateCancelButtonSx}>
            Cancel
          </Button>
          <Button onClick={saveSettings} disabled={settingsSaving || !canEdit} sx={harmonateProceedButtonSx}>
            {settingsSaving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(overrideDialog)}
        onClose={() => !overrideSaving && setOverrideDialog(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={harmonateDialogPaperProps}
      >
        <DialogTitle sx={harmonateDialogTitleSx}>Admin Override</DialogTitle>
        <DialogContent sx={harmonateDialogFormContentSx}>
          <Typography sx={{ color: ht.text, mb: 1 }}>
            Allow starting or changing {overrideDialog?.monthYear} even if it is{' '}
            {STATUS_META[overrideDialog?.status]?.label || overrideDialog?.status}.
          </Typography>
          {overrideDialog?.blockedMessage ? (
            <Alert severity="warning">{overrideDialog.blockedMessage}</Alert>
          ) : null}
          {!overrideDialog?.headerId ? (
            <Alert severity="info" sx={{ mt: 1 }}>
              No mapping header exists yet for this cell. Open Mapping / Account Selection to create it first, then
              right-click the cell again to override.
            </Alert>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setOverrideDialog(null)} disabled={overrideSaving} sx={harmonateCancelButtonSx}>
            Cancel
          </Button>
          <Button
            onClick={applyAdminOverride}
            disabled={overrideSaving || !overrideDialog?.headerId}
            sx={harmonateProceedButtonSx}
          >
            {overrideSaving ? 'Applying...' : 'Apply Override'}
          </Button>
        </DialogActions>
      </Dialog>

      <Toast
        open={toastState.open}
        onClose={hideToast}
        message={toastState.message}
        severity={toastState.severity}
        title={toastState.title}
        autoHideDuration={toastState.autoHideDuration}
        position={toastState.position}
      />
    </Box>
  );
};

export default CashReconStatusDashboard;
